import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, Share2, Link2Off, RotateCcw, Loader2, Camera, Lightbulb, MapPin, Clock, Users, AlertTriangle, Copy } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/sonner';
import { SEO } from '@/components/SEO';
import { Logo } from '@/components/Logo';
import {
  adminGetShoot,
  adminUpdateShoot,
  adminCreateShootShare,
  adminRevokeShootShare,
  sharedGetShoot,
  sharedUpdateShoot,
} from '@/lib/api';
import { SHOOT_MODE } from '@/constants/testIds';
import { ShootPositions } from '@/components/scout/ShootPositions';
import { keyedList } from '@/lib/keys';

const REFRESH_MS = 8000;

function useDebouncedCallback(fn, delay) {
  const t = useRef(null);
  return useCallback(
    (...args) => {
      if (t.current) clearTimeout(t.current);
      t.current = setTimeout(() => fn(...args), delay);
    },
    [fn, delay],
  );
}

const Panel = ({ icon: Icon, title, children }) => (
  <details className="group rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10]">
    <summary className="flex cursor-pointer list-none items-center gap-3 px-5 py-4 text-sm text-[hsl(var(--lxra-fg))]">
      <Icon className="h-4 w-4 text-[hsl(var(--lxra-gold))]" />
      <span className="flex-1 uppercase tracking-[0.12em] text-xs">{title}</span>
      <span className="text-[hsl(var(--lxra-muted))] text-xs group-open:hidden">Show</span>
      <span className="text-[hsl(var(--lxra-muted))] text-xs hidden group-open:inline">Hide</span>
    </summary>
    <div className="px-5 pb-5 text-sm text-[hsl(var(--lxra-muted))] leading-relaxed">{children}</div>
  </details>
);

/**
 * Shoot Mode — a mobile-first, tap-to-check shot list the photographer uses on
 * location. Works in two modes:
 *   - admin (mode="admin", :id)     — full access + share link controls
 *   - shared (mode="shared", :token) — second shooter via private link, no login
 */
export default function ShootMode({ mode = 'admin' }) {
  const { id, token } = useParams();
  const key = mode === 'admin' ? id : token;
  const [data, setData] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const notesDirty = useRef(false);

  // Stable per (mode, key) so hooks below can depend on it without re-running each render.
  const api = useMemo(
    () => (mode === 'admin'
      ? { get: () => adminGetShoot(key), update: (p) => adminUpdateShoot(key, p) }
      : { get: () => sharedGetShoot(key), update: (p) => sharedUpdateShoot(key, p) }),
    [mode, key],
  );

  const load = useCallback(async () => {
    try {
      const d = await api.get();
      setData(d);
      if (!notesDirty.current) setNotes(d.notes || '');
    } catch {
      setInvalid(true);
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  // Keep two shooters in sync: light polling while the page is open.
  useEffect(() => {
    if (invalid) return undefined;
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load, invalid]);

  const toggle = async (shotKey) => {
    if (!data) return;
    // optimistic
    const checked = { ...data.checked };
    if (checked[shotKey]) delete checked[shotKey];
    else checked[shotKey] = new Date().toISOString();
    setData({ ...data, checked, completed_shots: Object.keys(checked).length });
    try {
      const d = await api.update({ toggle: shotKey });
      setData(d);
    } catch {
      toast.error('Could not save. Check your connection.');
      load();
    }
  };

  const saveNotes = useDebouncedCallback(async (value) => {
    try {
      await api.update({ notes: value });
      notesDirty.current = false;
    } catch {
      toast.error('Could not save notes.');
    }
  }, 700);

  const onNotesChange = (e) => {
    notesDirty.current = true;
    setNotes(e.target.value);
    saveNotes(e.target.value);
  };

  const reset = async () => {
    if (!window.confirm('Clear all checked shots for this session?')) return;
    setBusy(true);
    try {
      setData(await api.update({ reset: true }));
      toast.success('Shot list reset.');
    } catch {
      toast.error('Could not reset.');
    } finally {
      setBusy(false);
    }
  };

  const shareUrl = data?.share_token ? `${window.location.origin}/shoot/${data.share_token}` : null;

  const createShare = async () => {
    setBusy(true);
    try {
      const res = await adminCreateShootShare(key);
      const url = `${window.location.origin}${res.path}`;
      setData((d) => ({ ...d, share_token: res.share_token }));
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Share link created and copied.');
      } catch {
        toast.success('Share link created.');
      }
    } catch {
      toast.error('Could not create share link.');
    } finally {
      setBusy(false);
    }
  };

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied.');
    } catch {
      toast.error('Could not copy link.');
    }
  };

  const revokeShare = async () => {
    if (!window.confirm('Revoke the share link? Anyone using it will lose access immediately.')) return;
    setBusy(true);
    try {
      await adminRevokeShootShare(key);
      setData((d) => ({ ...d, share_token: null }));
      toast.success('Share link revoked.');
    } catch {
      toast.error('Could not revoke link.');
    } finally {
      setBusy(false);
    }
  };

  if (invalid) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4" data-testid={SHOOT_MODE.root}>
        <div className="max-w-sm text-center rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-10" data-testid={SHOOT_MODE.invalid}>
          <AlertTriangle className="h-8 w-8 text-[hsl(8,55%,58%)] mx-auto mb-4" />
          <h1 className="font-display text-2xl tracking-[0.03em] text-[hsl(var(--lxra-fg))]">LINK UNAVAILABLE</h1>
          <p className="mt-3 text-sm text-[hsl(var(--lxra-muted))]">This shoot link is invalid or has been revoked by the studio.</p>
          <Link to="/" className="mt-6 inline-flex text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-gold))]">Back to LXRA Studios</Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] px-4 py-10" data-testid={SHOOT_MODE.root}>
        <div className="mx-auto max-w-xl space-y-4">
          <Skeleton className="h-8 w-40 rounded bg-[#141416]" />
          <Skeleton className="h-24 rounded-2xl bg-[#141416]" />
          <Skeleton className="h-64 rounded-2xl bg-[#141416]" />
        </div>
      </div>
    );
  }

  const pct = data.total_shots ? Math.round((data.completed_shots / data.total_shots) * 100) : 0;
  const done = data.total_shots > 0 && data.completed_shots === data.total_shots;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[hsl(var(--lxra-fg))] pb-28" data-testid={SHOOT_MODE.root}>
      <SEO title={`Shoot Mode — ${data.title || 'Session'}`} description="LXRA Studios Shoot Mode checklist." />

      {/* Sticky header with progress */}
      <header className="sticky top-0 z-40 bg-[#0A0A0A]/90 backdrop-blur border-b border-[hsl(var(--lxra-border))]">
        <div className="mx-auto max-w-xl px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            {mode === 'admin' ? (
              <Link to="/admin" data-testid={SHOOT_MODE.backLink} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))]">
                <ArrowLeft className="h-4 w-4" /> Dashboard
              </Link>
            ) : (
              <Logo />
            )}
            <span className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--lxra-gold))]">Shoot Mode</span>
          </div>
          <div className="mt-3 flex items-end justify-between gap-4">
            <div className="min-w-0">
              <h1 className="font-display text-2xl leading-none tracking-[0.03em] truncate">{data.title || 'SESSION'}</h1>
              <p className="text-xs text-[hsl(var(--lxra-muted))] mt-1 truncate">
                {data.client?.name} · {data.client?.occasion}
              </p>
            </div>
            <div className="text-right shrink-0" data-testid={SHOOT_MODE.progress} data-completed={data.completed_shots} data-total={data.total_shots}>
              <p className="font-display text-3xl leading-none text-[hsl(var(--lxra-gold))]">{data.completed_shots}<span className="text-[hsl(var(--lxra-muted))] text-xl">/{data.total_shots}</span></p>
              <p className="text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--lxra-muted))]">shots</p>
            </div>
          </div>
          <div className="mt-3 h-1.5 w-full rounded-full bg-[#141416] overflow-hidden">
            <motion.div className="h-full bg-[hsl(var(--lxra-gold))]" animate={{ width: `${pct}%` }} transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }} />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-xl px-4 py-6 space-y-6">
        {done && (
          <div className="rounded-2xl border border-[hsl(var(--lxra-gold))]/50 bg-[hsl(var(--lxra-gold))]/10 p-5 text-center" data-testid="shoot-mode-complete">
            <Check className="h-6 w-6 text-[hsl(var(--lxra-gold))] mx-auto" />
            <p className="mt-2 font-display text-xl tracking-[0.03em]">EVERY SHOT CAPTURED.</p>
            <p className="text-xs text-[hsl(var(--lxra-muted))] mt-1">Nice work. Grab a few candids on the way out.</p>
          </div>
        )}

        {/* Session essentials */}
        <div className="grid grid-cols-2 gap-3 text-xs">
          {[
            [Clock, data.client?.time_of_day, data.client?.session_date || 'date flexible'],
            [Users, `${data.client?.people_count || 1} in frame`, data.client?.location_preference || 'location flexible'],
          ].map(([Icon, a, b]) => (
            <div key={`${a}-${b}`} className="rounded-xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-3 flex items-start gap-2.5">
              <Icon className="h-4 w-4 text-[hsl(var(--lxra-gold))] mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="text-[hsl(var(--lxra-fg))] capitalize truncate">{a}</p>
                <p className="text-[hsl(var(--lxra-muted))] truncate">{b}</p>
              </div>
            </div>
          ))}
        </div>
        {data.scout && <ShootPositions scout={data.scout} mode={mode} shootKey={key} />}
        {data.scout && (
          <Link
            to={mode === 'admin' ? `/admin/scout/${data.scout.id}` : '#'}
            onClick={(e) => mode !== 'admin' && e.preventDefault()}
            data-testid="shoot-mode-scout-card"
            className={`block rounded-2xl border border-[hsl(var(--lxra-gold))]/40 bg-[#0F0F10] overflow-hidden ${mode === 'admin' ? 'hover:border-[hsl(var(--lxra-gold))]' : ''} transition-colors`}
          >
            <div className="flex">
              {data.scout.concept_image_path && (
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/media/${data.scout.concept_image_path}`} alt="" className="w-28 object-cover shrink-0" />
              )}
              <div className="p-3.5 min-w-0 flex-1">
                <p className="text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--lxra-gold))]">Location Scout</p>
                <p className="text-sm text-[hsl(var(--lxra-fg))] truncate mt-0.5">{data.scout.place_name}</p>
                <p className="text-[11px] text-[hsl(var(--lxra-muted))] mt-1">
                  {data.scout.sun_at_session ? `Sun ${data.scout.sun_at_session.direction} · ${data.scout.sun_at_session.elevation}° at ${data.scout.sun_at_session.clock}` : `Sunset ${data.scout.sunset}`}
                  {data.scout.weather_summary ? ` · ${data.scout.weather_summary}` : ''}
                </p>
                {data.scout.best_windows?.[0] && (
                  <p className="text-[11px] text-[hsl(var(--lxra-fg))] mt-1 truncate">Best: {data.scout.best_windows.find((w) => w.priority === 'primary')?.start || data.scout.best_windows[0].start}–{(data.scout.best_windows.find((w) => w.priority === 'primary') || data.scout.best_windows[0]).end}</p>
                )}
                {data.scout.sun_strategy && <p className="text-[11px] text-[hsl(var(--lxra-muted))] mt-1 line-clamp-2">{data.scout.sun_strategy}</p>}
              </div>
            </div>
          </Link>
        )}
        {data.client?.must_have_shots && (
          <p className="rounded-xl border border-[hsl(var(--lxra-gold))]/30 bg-[#0F0F10] p-3 text-xs text-[hsl(var(--lxra-muted))]">
            <span className="text-[hsl(var(--lxra-gold))] uppercase tracking-[0.12em]">Client must-haves: </span>
            {data.client.must_have_shots}
          </p>
        )}

        {/* Shot list */}
        <section className="space-y-4">
          {keyedList(data.shot_list, (s) => s.scene).map(({ key, item: scene }, si) => {
            const sceneDone = scene.shots.filter((_, i) => data.checked[`${si}-${i}`]).length;
            return (
              <div key={key} className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] overflow-hidden" data-testid={SHOOT_MODE.scene(si)}>
                <div className="flex items-center gap-3 px-4 py-3 border-b border-[hsl(var(--lxra-border))]">
                  <span className="font-display text-xl text-[hsl(var(--lxra-gold))]">{String(si + 1).padStart(2, '0')}</span>
                  <div className="min-w-0 flex-1">
                    <h2 className="font-display text-lg leading-tight tracking-[0.03em] truncate">{scene.scene}</h2>
                    {scene.location_hint && <p className="text-[11px] text-[hsl(var(--lxra-muted))] truncate flex items-center gap-1"><MapPin className="h-3 w-3" />{scene.location_hint}</p>}
                  </div>
                  <span className={`text-xs ${sceneDone === scene.shots.length ? 'text-[hsl(var(--lxra-gold))]' : 'text-[hsl(var(--lxra-muted))]'}`}>{sceneDone}/{scene.shots.length}</span>
                </div>
                <ul>
                  {scene.shots.map((shot, i) => {
                    const k = `${si}-${i}`;
                    const isChecked = Boolean(data.checked[k]);
                    return (
                      <li key={k} className="border-b border-[hsl(var(--lxra-border))] last:border-b-0">
                        <button
                          type="button"
                          onClick={() => toggle(k)}
                          aria-pressed={isChecked}
                          data-testid={SHOOT_MODE.shot(k)}
                          className={`w-full flex items-start gap-3 px-4 py-3.5 text-left min-h-[56px] transition-colors active:bg-[#141416] ${isChecked ? 'bg-[hsl(var(--lxra-gold))]/[0.06]' : ''}`}
                        >
                          <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-colors ${
                            isChecked ? 'bg-[hsl(var(--lxra-gold))] border-[hsl(var(--lxra-gold))] text-[#0A0A0A]' : 'border-[hsl(var(--lxra-muted))]/60'
                          }`}>
                            {isChecked && <Check className="h-3.5 w-3.5" />}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-2">
                              <span className={`text-sm ${isChecked ? 'line-through text-[hsl(var(--lxra-muted))]' : 'text-[hsl(var(--lxra-fg))]'}`}>{shot.title}</span>
                              {shot.priority === 'must' && !isChecked && <span className="rounded-full border border-[hsl(var(--lxra-gold))]/40 px-1.5 text-[9px] uppercase tracking-[0.1em] text-[hsl(var(--lxra-gold))]">must</span>}
                            </span>
                            {shot.description && <span className={`block text-xs mt-0.5 leading-relaxed ${isChecked ? 'text-[hsl(var(--lxra-muted))]/60' : 'text-[hsl(var(--lxra-muted))]'}`}>{shot.description}</span>}
                            <span className="block text-[10px] uppercase tracking-[0.12em] text-[hsl(var(--lxra-gold))]/70 mt-1">{shot.framing}</span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </section>

        {/* Reference panels */}
        <section className="space-y-3">
          <Panel icon={Lightbulb} title="Lighting plan">
            <dl className="space-y-2">
              {Object.entries(data.lighting || {}).filter(([, v]) => v).map(([k, v]) => (
                <div key={k}><dt className="text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--lxra-gold))]/80">{k.replace('_', ' ')}</dt><dd className="text-[hsl(var(--lxra-fg))]">{v}</dd></div>
              ))}
            </dl>
          </Panel>
          <Panel icon={Users} title="Pose cues">
            <ul className="space-y-2">
              {(data.poses || []).map((p) => (
                <li key={p.name}><span className="text-[hsl(var(--lxra-fg))]">{p.name}</span> — {p.description}</li>
              ))}
            </ul>
          </Panel>
          <Panel icon={Clock} title="Timeline">
            <ul className="space-y-1.5">
              {keyedList(data.timeline, (t) => `${t.time}-${t.activity}`).map(({ key, item: t }) => (
                <li key={key}><span className="text-[hsl(var(--lxra-gold))]">{t.time}</span> · {t.activity} <span className="text-[hsl(var(--lxra-muted))]/70">({t.duration_minutes}m)</span></li>
              ))}
            </ul>
          </Panel>
          {data.photographer_notes && (
            <Panel icon={Camera} title="Photographer notes (private)">
              <p className="whitespace-pre-wrap">{data.photographer_notes}</p>
            </Panel>
          )}
        </section>

        {/* Field notes */}
        <section>
          <label htmlFor="shoot-notes" className="text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))]">Field notes</label>
          <Textarea
            id="shoot-notes"
            rows={4}
            value={notes}
            onChange={onNotesChange}
            placeholder="Weather, timing changes, extra shots captured..."
            data-testid={SHOOT_MODE.notes}
            className="mt-1.5 bg-[#0F0F10] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] focus-visible:ring-[hsl(var(--lxra-gold))] resize-y"
          />
          <p className="mt-1 text-[10px] text-[hsl(var(--lxra-muted))]">Saves automatically. Shared with everyone on this link.</p>
        </section>
      </main>

      {/* Bottom action bar */}
      <div className="fixed bottom-0 inset-x-0 z-40 bg-[#0A0A0A]/95 backdrop-blur border-t border-[hsl(var(--lxra-border))]">
        <div className="mx-auto max-w-xl px-4 py-3 flex items-center gap-2">
          {mode === 'admin' ? (
            shareUrl ? (
              <>
                <button type="button" onClick={copyShare} className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] text-xs uppercase tracking-[0.1em] font-semibold" data-testid="shoot-mode-copy-share-button">
                  <Copy className="h-4 w-4" /> Copy Share Link
                </button>
                <button type="button" onClick={revokeShare} disabled={busy} data-testid={SHOOT_MODE.revokeButton} className="inline-flex items-center justify-center gap-2 h-11 px-4 rounded-xl border border-[hsl(var(--lxra-border))] text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))] hover:text-[hsl(8,55%,68%)] disabled:opacity-50">
                  <Link2Off className="h-4 w-4" /> Revoke
                </button>
              </>
            ) : (
              <button type="button" onClick={createShare} disabled={busy} data-testid={SHOOT_MODE.shareButton} className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-xl border border-[hsl(var(--lxra-gold))]/60 text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />} Share with Second Shooter
              </button>
            )
          ) : (
            <p className="flex-1 text-xs text-[hsl(var(--lxra-muted))]">Second-shooter link · progress syncs with the studio</p>
          )}
          <button type="button" onClick={reset} disabled={busy || !data.completed_shots} data-testid={SHOOT_MODE.resetButton} aria-label="Reset checklist" className="inline-flex items-center justify-center h-11 w-11 rounded-xl border border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))] disabled:opacity-40">
            <RotateCcw className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
